import { PlusSquareIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FC, useCallback, useState } from 'react';
import { INSERT_IMAGE_COMMAND } from '../../plugins';
import { ToolbarButton, ToolbarButtonProps } from './ToolbarButton';
import { useTranslation } from 'react-i18next';

type ImageButtonProps = Omit<ToolbarButtonProps, 'children'>;

export const ImageButton: FC<ImageButtonProps> = ({ ...rest }) => {
  const { t } = useTranslation('html-editor');
  const [editor] = useLexicalComposerContext();
  const [url, setUrl] = useState('');

  const { isOpen, onToggle, onClose } = useDisclosure();

  const handleApply = useCallback(() => {
    if (url) {
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, { src: url });
    }
  }, [editor, url]);

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom">
      <PopoverTrigger>
        <ToolbarButton onClick={onToggle} title={t('htmlEditor.toolbar.image')} {...rest}>
          <PlusSquareIcon />
        </ToolbarButton>
      </PopoverTrigger>
      {/* in a portal so it doesn't interfere with form validation when included in a form (i.e. in the admin) */}
      <Portal>
        <PopoverContent>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader>{t('htmlEditor.imagePopup.title')}</PopoverHeader>
          <PopoverBody>
            <VStack alignItems="stretch" spacing={2}>
              <FormControl isRequired>
                <FormLabel size="sm">{t('htmlEditor.imagePopup.url')}</FormLabel>
                <Input type="url" size="sm" value={url} onChange={(e) => setUrl(e.currentTarget.value)} />
              </FormControl>
              <Flex>
                <Button type="button" colorScheme="primary" onClick={handleApply} ml="auto">
                  {t('htmlEditor.imagePopup.insert')}
                </Button>
              </Flex>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
